import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Button,
  FormField,
  FormSelect,
} from "@/components/ui";
import {
  OutreachFormSchema,
  TIERS,
  TIER_LABELS,
  EMPTY_OUTREACH_FORM_VALUES,
  type OutreachFormValues,
} from "@/api/types/outreach";
import { useCreateOutreach } from "@/api/hooks/useOutreach";
import { getErrorMessage } from "@/api/client";
import { toastError, toastSuccess } from "@/components/ui/Toast";

interface OutreachFormDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * "New outreach target": name + tier only. Creates the contact inline —
 * mirrors InquiryFormDialog's bare-contact_name convention rather than a
 * contact picker (no search/combobox component exists in the UI kit yet).
 */
export function OutreachFormDialog({ open, onClose }: OutreachFormDialogProps) {
  const createOutreach = useCreateOutreach();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<OutreachFormValues>({
    resolver: zodResolver(OutreachFormSchema),
    defaultValues: EMPTY_OUTREACH_FORM_VALUES,
  });

  useEffect(() => {
    if (open) reset(EMPTY_OUTREACH_FORM_VALUES);
  }, [open, reset]);

  const onSubmit = handleSubmit(async (values) => {
    try {
      await createOutreach.mutateAsync(values);
      toastSuccess("Outreach target added");
      onClose();
    } catch (err) {
      toastError("Couldn't add outreach target", getErrorMessage(err));
    }
  });

  return (
    <Dialog open={open} onClose={onClose} ariaLabel="New outreach target">
      <DialogContent size="sm">
        {/* noValidate: zod/RHF owns validation (see ContactFormDialog for why). */}
        <form onSubmit={onSubmit} noValidate>
          <DialogHeader onClose={onClose}>New outreach target</DialogHeader>
          <DialogBody className="space-y-4">
            <FormField
              label="Contact name"
              required
              error={errors.name?.message}
              {...register("name")}
            />
            <FormSelect label="Tier" required error={errors.tier?.message} {...register("tier")}>
              {TIERS.map((t) => (
                <option key={t} value={t}>
                  {TIER_LABELS[t]}
                </option>
              ))}
            </FormSelect>
          </DialogBody>
          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={createOutreach.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createOutreach.isPending}>
              {createOutreach.isPending ? "Saving..." : "Add target"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
